import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { useCurrency } from "../../context/CurrencyContext";
import { formatFromInr } from "../../lib/currency";
import {
  describeTimeZone,
  localizeScheduleEntries,
  SOURCE_TIME_ZONE,
  SOURCE_TIME_ZONE_LABEL,
} from "../../lib/localSchedule";

const MAX_VISIBLE_SLOTS = 3;

export default function OnlineTeacherCard({ teacher, onBookConsultation, viewerTimeZone }) {
  const { currency, rates } = useCurrency();
  const priceLabel =
    teacher.minPriceInr != null
      ? `From ${formatFromInr(teacher.minPriceInr, currency, rates)} / ${teacher.priceUnit || "session"}`
      : null;

  const specializationLine = (teacher.specializations || []).join(" · ");

  // Falls back to the teacher's own timezone until the viewer's is known after
  // mount, so the prerendered markup and the first client render agree.
  const localSlots = useMemo(
    () => localizeScheduleEntries(teacher.scheduleEntries, viewerTimeZone || SOURCE_TIME_ZONE),
    [teacher.scheduleEntries, viewerTimeZone]
  );

  const visibleSlots = localSlots.slice(0, MAX_VISIBLE_SLOTS);
  const hiddenSlotCount = localSlots.length - visibleSlots.length;
  const zoneLabel = viewerTimeZone
    ? describeTimeZone(viewerTimeZone)
    : SOURCE_TIME_ZONE_LABEL;

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-xl focus-within:ring-2 focus-within:ring-emerald-500 focus-within:ring-offset-2">
      <div className="relative aspect-[4/5] overflow-hidden bg-gray-100 sm:aspect-[3/4]">
        <img
          src={teacher.photo}
          alt=""
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <h3 className="text-xl font-bold tracking-tight text-gray-900 group-hover:text-emerald-800">
          {teacher.name}
        </h3>

        {specializationLine ? (
          <p className="mt-1.5 text-sm font-medium text-emerald-700">{specializationLine}</p>
        ) : null}

        {visibleSlots.length ? (
          <div className="mt-4 rounded-xl bg-emerald-50 p-3.5 ring-1 ring-emerald-100">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-800">
              <Clock size={13} strokeWidth={2.5} />
              {viewerTimeZone ? `In your timezone · ${zoneLabel}` : `Class times (${zoneLabel})`}
            </p>
            <ul className="mt-2 space-y-1">
              {visibleSlots.map((slot) => (
                <li key={`${slot.days}-${slot.time}`} className="text-sm text-emerald-950">
                  <span className="font-semibold">{slot.days}</span>
                  <span className="text-emerald-800"> · {slot.time}</span>
                </li>
              ))}
            </ul>
            {hiddenSlotCount > 0 ? (
              <p className="mt-1.5 text-xs font-medium text-emerald-700">
                +{hiddenSlotCount} more class {hiddenSlotCount === 1 ? "time" : "times"}
              </p>
            ) : null}
          </div>
        ) : null}

        {teacher.intro ? (
          <p className="mt-3 text-sm leading-relaxed text-gray-600 line-clamp-3">{teacher.intro}</p>
        ) : null}

        <div className="mt-4 space-y-1.5 text-xs text-gray-500">
          {teacher.experienceLabel ? <p>{teacher.experienceLabel}</p> : null}
          {teacher.certifications?.length ? (
            <p className="line-clamp-2">{teacher.certifications.join(" · ")}</p>
          ) : null}
          {teacher.languages?.length ? <p>Languages: {teacher.languages.join(", ")}</p> : null}
        </div>

        {teacher.formats?.length ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {teacher.formats.map((format) => (
              <span
                key={format}
                className="rounded-md bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-100"
              >
                {format}
              </span>
            ))}
          </div>
        ) : null}

        <p className="mt-4 text-sm font-semibold text-gray-900">
          {priceLabel || "Price on request"}
        </p>

        <div className="relative z-10 mt-5 flex flex-col gap-2 sm:flex-row">
          {teacher.profilePath ? (
            <span className="inline-flex flex-1 items-center justify-center rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors group-hover:bg-emerald-700">
              View Teacher Profile
            </span>
          ) : null}
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onBookConsultation(teacher);
            }}
            className="inline-flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
          >
            Book Consultation
          </button>
        </div>
      </div>

      {teacher.profilePath ? (
        <Link
          to={teacher.profilePath}
          className="absolute inset-0 z-[1] cursor-pointer"
          aria-label={`View ${teacher.name}'s profile`}
        />
      ) : null}
    </article>
  );
}
